import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Check, Copy, Play, Server as ServerIcon, MapPin } from 'lucide-react';
import { db } from '../../firebase/config';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { buildConnectCommand, buildSteamLink } from '../../lib/servers';
import SEO from '../SEO';

const ServerCard = ({ server }) => {
  const [copied, setCopied] = useState(false);
  const connect = buildConnectCommand(server.host, server.port);
  const steam = buildSteamLink(server.host, server.port);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(connect);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the field is selectable as a fallback.
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ServerIcon className="h-4 w-4 text-primary" />
            {server.name}
          </CardTitle>
          {server.region && (
            <Badge variant="secondary" className="gap-1">
              <MapPin className="h-3 w-3" />
              {server.region}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="mt-auto space-y-4">
        <div className="space-y-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Console command
          </span>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 select-all overflow-x-auto whitespace-nowrap rounded-md border bg-muted px-3 py-2 font-mono text-sm">
              {connect}
            </code>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleCopy}
              aria-label="Copy connect command"
            >
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className={cn('text-xs text-muted-foreground', !copied && 'invisible')}>
            Copied to clipboard
          </p>
        </div>

        <Button asChild className="w-full">
          <a href={steam}>
            <Play className="h-4 w-4" />
            Join via Steam
          </a>
        </Button>
      </CardContent>
    </Card>
  );
};

const Servers = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServers = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'servers'), orderBy('order', 'asc')));
        setServers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        // Network/permission failure — render the empty state.
      } finally {
        setLoading(false);
      }
    };
    fetchServers();
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <SEO
        title="Game Servers"
        description="Connect to the official KZCOMP CS2KZ game servers. Copy the console command or join directly through Steam."
        canonicalPath="/servers"
      />

      <header className="mb-8 space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Game Servers</h1>
        <p className="text-muted-foreground">
          Jump into our official CS2KZ servers. Copy the console command and paste it into the
          in-game developer console, or hit{' '}
          <span className="font-medium text-foreground">Join via Steam</span> to connect directly.
        </p>
      </header>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-md border bg-muted/40" />
          ))}
        </div>
      ) : servers.length === 0 ? (
        <div className="rounded-md border border-dashed px-6 py-16 text-center text-muted-foreground">
          No servers are online right now. Check back soon.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {servers.map((server) => (
            <ServerCard key={server.id} server={server} />
          ))}
        </div>
      )}

      <p className="mt-8 text-sm text-muted-foreground">
        Tip: enable the developer console in CS2 under{' '}
        <span className="font-medium text-foreground">
          Settings → Game → Enable Developer Console
        </span>
        , then press{' '}
        <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">~</kbd> and paste
        the command.
      </p>
    </div>
  );
};

export default Servers;
